import { useKeenSlider } from "keen-slider/react";
import React, { useState } from "react";
import BannerCard from "./BannerCard";
import { useRequestAnimationFrame } from "@/hooks/useRequestAnimationFrame";
import { useDebounce } from "@/hooks/useDebounce";

interface MobileCardProps {
  data?: any;
}

const MobileCardSlider: React.FC<MobileCardProps> = ({ data }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    mode: "snap", // Better performance on mobile
    rubberband: false, // Reduce jank
    slides: {
      perView: 1.2,
      spacing: 15,
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
    dragStarted() {
      // Pause auto-play when user interacts
      setAutoPlayEnabled(false);
    },
    dragEnded() {
      // Resume auto-play after interaction
      setAutoPlayEnabled(true);
    },
  });

  // Use requestAnimationFrame for smoother auto-play with less main thread blocking
  useRequestAnimationFrame(
    () => {
      instanceRef.current?.next();
    },
    3000, // 3 seconds
    autoPlayEnabled && loaded
  );

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove special chars
      .replace(/\s+/g, "-"); // spaces → dash

  // Debounced scroll handler to prevent rapid clicks from blocking main thread
  const handleCardClick = useDebounce((cardId: string, cardTitle: string) => {
    const target = document.getElementById(cardId || slugify(cardTitle));

    if (target) {
      const y = target.getBoundingClientRect().top + window.scrollY - 350; // offset for header + mobile stacked cards
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, 100);

  return (
    <>
      <div className="block mt-[-70px]">
        <div ref={sliderRef} className="keen-slider" style={{ willChange: 'transform' }}>
          {data?.bannerCard.map((card: any, index: number) => (
            <div
              className="keen-slider__slide"
              key={card.id}
              style={{ willChange: 'transform' }}
            >
              <BannerCard
                key={index}
                title={card.title}
                theme={index % 2 === 0 ? "blue" : "green"} // blue, green, blue...
                onclick={() => {
                  handleCardClick(
                    card?.link?.href?.replace("#", "") || "",
                    card.title
                  );
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden block">
        {loaded && instanceRef.current && (
          <div className="dots container">
            {Array.from({
              length: instanceRef?.current?.track?.details?.slides?.length,
            }).map((_, idx) => (
              <button
                aria-label={`Go to slide ${idx + 1}`}
                key={idx}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
                className={`dot${currentSlide === idx ? " active" : ""}`}
              >
                <span className={currentSlide === idx ? "active" : ""}></span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MobileCardSlider;

